import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import * as RadixTabs from '@radix-ui/react-tabs';
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type SyntheticEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, NavLink, useSearchParams } from 'react-router';

import { isTyping } from './keyboard.js';
import { hotkeyLabel, matchesHotkey, pickTab } from './logic.js';

export { nextIndex, useListKeyboard } from './keyboard.js';

/**
 * Patrons d'interface communs (docs/ux/composants.md § Patrons) : onglets instantanés gardés
 * dans l'adresse, fil d'Ariane, menu d'actions groupées, filtres en pastilles, raccourcis
 * clavier, interrupteurs dans la ligne, édition sur place, réglages en lignes compactes
 * (sections titrées, avancés repliés, zone sensible) et barre d'enregistrement collante.
 * Inspirés des back-offices éprouvés (Zendesk, Linear, Odoo) : moins de clics, moins de
 * défilement. Classes `o-*` dans `patterns.css`, jetons seulement.
 */

/* ------------------------------------------------------------------ */
/* Raccourcis clavier                                                  */
/* ------------------------------------------------------------------ */

const isMac = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Raccourci clavier global de l'écran (`mod+s` = Ctrl/⌘+S, `n`, `/`, `escape`). Un
 * raccourci sans Ctrl/⌘ est ignoré pendant la saisie dans un champ.
 */
export function useHotkey(
  spec: string,
  handler: (event: KeyboardEvent) => void,
  enabled = true,
): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (!matchesHotkey(spec, event)) return;
      const withMod = event.ctrlKey || event.metaKey;
      if (!withMod && spec !== 'escape' && isTyping(event.target)) return;
      event.preventDefault();
      ref.current(event);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [spec, enabled]);
}

/** Touche(s) d'un raccourci, affichée à côté d'une action (`<kbd>`). */
export function Kbd({ keys }: { keys: string }): ReactElement {
  return <kbd className="o-kbd">{hotkeyLabel(keys, isMac())}</kbd>;
}

/* ------------------------------------------------------------------ */
/* Fil d'Ariane et onglets                                             */
/* ------------------------------------------------------------------ */

export interface Crumb {
  label: string;
  /** Absent : élément courant. */
  to?: string | undefined;
  /** État transmis au lien (chemin d'arrivée, `TRAIL_STATE`). */
  state?: unknown;
}

/**
 * Fil d'Ariane : section › page › onglet. Un élément sans `to` est l'élément courant ; sur
 * une fiche ouverte depuis une autre (lien d'entité), il reprend le chemin d'arrivée.
 */
export function Breadcrumb({ items }: { items: readonly Crumb[] }): ReactElement | null {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Fil d'Ariane" className="o-breadcrumb">
      <ol>
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${item.label}-${String(i)}`}>
              {item.to !== undefined ? (
                <Link to={item.to} state={item.state}>
                  {item.label}
                </Link>
              ) : (
                <span aria-current={last ? 'page' : undefined}>{item.label}</span>
              )}
              {!last && <ChevronRight aria-hidden="true" size={14} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface PageTab {
  to: string;
  label: string;
  icon?: LucideIcon | undefined;
  count?: number | undefined;
}

/**
 * Onglets d'une page faits de routes (chaque onglet a son adresse, ses droits, son aide) :
 * changement instantané, sans rechargement ; l'onglet courant est marqué `aria-current`.
 * La coque les affiche d'office pour les entrées de menu regroupées.
 */
export function PageTabs({
  tabs,
  label,
}: {
  tabs: readonly PageTab[];
  label: string;
}): ReactElement | null {
  if (tabs.length < 2) return null;
  return (
    <nav aria-label={label} className="o-page-tabs">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <NavLink key={t.to} to={t.to} end className="o-page-tabs__tab">
            {Icon !== undefined && <Icon aria-hidden="true" size={16} />}
            <span>{t.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export interface TabItem {
  key: string;
  label: string;
  icon?: LucideIcon | undefined;
  count?: number | undefined;
  /** Onglet absent (droit manquant, module inactif) : ni bouton ni contenu. */
  hidden?: boolean | undefined;
  /** Pastille « à traiter » à côté du libellé (points à vérifier dans l'onglet). */
  alert?: boolean | undefined;
  content: ReactNode;
}

/**
 * Onglets dans une page, état gardé dans l'adresse (`?onglet=…`, remplacé sans nouvelle
 * entrée d'historique) : un lien ou un rechargement rouvre le même onglet. Clavier : flèches,
 * Début, Fin (Radix). Les onglets masqués conservent leur saisie (`forceMount` facultatif).
 */
export function Tabs({
  tabs,
  label,
  param = 'onglet',
  keepMounted = false,
  mount,
}: {
  /** Les entrées `false`/`null` sont ignorées (onglet conditionnel écrit en ligne). */
  tabs: readonly (TabItem | false | null | undefined)[];
  label: string;
  /** Paramètre d'adresse ; deux jeux d'onglets d'une même page prennent deux noms. */
  param?: string;
  /** Garder montés les onglets cachés (formulaire réparti sur plusieurs onglets). */
  keepMounted?: boolean;
  /**
   * Montage des panneaux : `active` (seul l'onglet ouvert), `all` (= `keepMounted`), ou
   * `visited` — monté à la première ouverture puis gardé (fiches : les requêtes d'un onglet
   * ne retardent pas la fiche, une saisie n'est pas perdue en changeant d'onglet).
   */
  mount?: 'active' | 'visited' | 'all' | undefined;
}): ReactElement {
  const [params, setParams] = useSearchParams();
  const visible = tabs.filter((t): t is TabItem => t !== false && t != null && t.hidden !== true);
  const current = pickTab(
    visible.map((t) => t.key),
    params.get(param),
  );
  const mode = mount ?? (keepMounted ? 'all' : 'active');
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set([current]));
  if (mode === 'visited' && !visited.has(current)) setVisited(new Set([...visited, current]));
  return (
    <RadixTabs.Root
      className="o-tabs"
      value={current}
      onValueChange={(value) => {
        const next = new URLSearchParams(params);
        if (value === visible[0]?.key) next.delete(param);
        else next.set(param, value);
        setParams(next, { replace: true, preventScrollReset: true });
      }}
    >
      <RadixTabs.List className="o-tabs__list" aria-label={label}>
        {visible.map((t) => {
          const Icon = t.icon;
          return (
            <RadixTabs.Trigger key={t.key} value={t.key} className="o-tabs__trigger">
              {Icon !== undefined && <Icon aria-hidden="true" size={16} />}
              {t.label}
              {/* Pas de pastille de nombre sur un onglet : elle se lisait comme un nombre de
                  tâches (audit rc.5). Le nombre est donné dans l'onglet (`ListHeader`). */}
              {t.alert === true && (
                <span className="o-tabs__alert" role="img" aria-label="à traiter" />
              )}
            </RadixTabs.Trigger>
          );
        })}
      </RadixTabs.List>
      {visible.map((t) => (
        <RadixTabs.Content
          key={t.key}
          value={t.key}
          className="o-tabs__panel"
          {...(mode === 'active' ? {} : { forceMount: true, hidden: t.key !== current })}
        >
          {mode === 'visited' && !visited.has(t.key) && t.key !== current ? null : t.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}

/* ------------------------------------------------------------------ */
/* Actions groupées et filtres en pastilles                            */
/* ------------------------------------------------------------------ */

export type ActionItem =
  | {
      label: string;
      icon?: LucideIcon | undefined;
      onSelect?: (() => void) | undefined;
      to?: string | undefined;
      danger?: boolean | undefined;
      disabled?: boolean | undefined;
      /** Raccourci affiché (le brancher avec `useHotkey`). */
      shortcut?: string | undefined;
      hidden?: boolean | undefined;
    }
  | 'separator';

/**
 * Menu d'actions groupées (« ⋯ » ou « Actions ▾ ») au lieu d'une rangée de boutons : une
 * action principale reste un bouton à côté, les secondaires et sensibles vont ici.
 */
export function ActionMenu({
  items,
  label = 'Actions',
  iconOnly = false,
  align = 'end',
}: {
  items: readonly ActionItem[];
  label?: string;
  /** Bouton « ⋯ » seul (lignes de tableau) ; le libellé reste le nom accessible. */
  iconOnly?: boolean;
  align?: 'start' | 'end';
}): ReactElement | null {
  const shown = items.filter((i) => i === 'separator' || i.hidden !== true);
  // Pas de séparateur en tête, en fin ni doublé.
  const clean = shown.filter(
    (i, n) => i !== 'separator' || (n > 0 && n < shown.length - 1 && shown[n - 1] !== 'separator'),
  );
  if (!clean.some((i) => i !== 'separator')) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={
            iconOnly ? 'o-button o-button--ghost o-icon-button' : 'o-button o-action-menu__trigger'
          }
          aria-label={iconOnly ? label : undefined}
        >
          {iconOnly ? (
            <MoreHorizontal aria-hidden="true" size={18} />
          ) : (
            <>
              {label}
              <ChevronDown aria-hidden="true" size={16} />
            </>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="o-menu" align={align} sideOffset={6}>
          {clean.map((item, n) => {
            if (item === 'separator')
              return (
                <DropdownMenu.Separator key={`sep-${String(n)}`} className="o-menu__separator" />
              );
            const Icon = item.icon;
            const body = (
              <>
                {Icon !== undefined && <Icon aria-hidden="true" size={16} />}
                <span className="o-menu__label">{item.label}</span>
                {item.shortcut !== undefined && <Kbd keys={item.shortcut} />}
              </>
            );
            const className =
              item.danger === true ? 'o-menu__item o-menu__item--danger' : 'o-menu__item';
            return item.to !== undefined ? (
              <DropdownMenu.Item
                key={item.label}
                className={className}
                disabled={item.disabled}
                asChild
              >
                <Link to={item.to}>{body}</Link>
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Item
                key={item.label}
                className={className}
                disabled={item.disabled}
                onSelect={() => item.onSelect?.()}
              >
                {body}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export interface ChipOption {
  value: string;
  label: string;
  count?: number | undefined;
}

/**
 * Filtre en pastilles (statut, type, période) : un clic filtre, la pastille active est
 * marquée `aria-pressed`. `multiple` : plusieurs valeurs ; sinon un nouveau clic sur la
 * pastille active la retire (valeur vide = « Tous »).
 */
export function ChipFilter({
  label,
  options,
  value,
  onChange,
  multiple = false,
  allLabel = 'Tous',
}: {
  label: string;
  options: readonly ChipOption[];
  value: string | readonly string[];
  onChange: (value: string[] | string) => void;
  multiple?: boolean;
  /** Pastille « tout » en tête (`null` : aucune). */
  allLabel?: string | null;
}): ReactElement {
  const selected = new Set(typeof value === 'string' ? (value === '' ? [] : [value]) : value);
  const toggle = (v: string): void => {
    if (multiple) {
      const next = new Set(selected);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      onChange([...next]);
    } else onChange(selected.has(v) ? '' : v);
  };
  return (
    <div className="o-chips" role="group" aria-label={label}>
      {allLabel !== null && (
        <button
          type="button"
          className="o-chip"
          aria-pressed={selected.size === 0}
          onClick={() => {
            onChange(multiple ? [] : '');
          }}
        >
          {allLabel}
        </button>
      )}
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="o-chip"
          aria-pressed={selected.has(o.value)}
          onClick={() => {
            toggle(o.value);
          }}
        >
          {o.label}
          {o.count !== undefined && <span className="o-chip__count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Contrôles dans la ligne                                             */
/* ------------------------------------------------------------------ */

/**
 * Interrupteur (oui / non appliqué tout de suite ou à l'enregistrement) : `role="switch"`,
 * libellé visible ou `aria-label` quand la ligne porte déjà le libellé (`SettingRow`).
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  id,
  describedBy,
  showState = true,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Nom accessible (texte visible si `showState` est faux). */
  label: string;
  disabled?: boolean;
  id?: string | undefined;
  describedBy?: string | undefined;
  /** Écrit « Activé / Désactivé » à côté (la couleur n'est jamais seule). */
  showState?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      className="o-switch"
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className="o-switch__track" aria-hidden="true">
        <span className="o-switch__thumb" />
      </span>
      {showState && (
        <span className="o-switch__state" aria-hidden="true">
          {checked ? 'Activé' : 'Désactivé'}
        </span>
      )}
    </button>
  );
}

/**
 * Choix segmenté (2 à 4 options courtes) dans la ligne, au lieu d'une liste déroulante :
 * un clic, valeur visible d'un coup d'œil.
 */
export function Segmented<V extends string>({
  label,
  options,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  options: readonly { value: V; label: string }[];
  value: V;
  onChange: (value: V) => void;
  disabled?: boolean;
}): ReactElement {
  return (
    <div className="o-segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={disabled}
          className="o-segmented__option"
          onClick={() => {
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Édition sur place d'un texte court (libellé, nom, valeur) : clic sur « Modifier »,
 * Entrée enregistre, Échap annule. Pour plus d'un champ, un `Dialog` ou un formulaire.
 */
export function InlineEdit({
  value,
  label,
  onSave,
  placeholder = '—',
  type = 'text',
  disabled = false,
}: {
  value: string;
  /** Nom du champ (bouton « Modifier <label> », champ étiqueté). */
  label: string;
  onSave: (next: string) => unknown;
  placeholder?: string;
  type?: 'text' | 'number' | 'email' | 'url';
  disabled?: boolean;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);
  if (!editing)
    return (
      <span className="o-inline-edit">
        <span className={value === '' ? 'o-inline-edit__value o-hint' : 'o-inline-edit__value'}>
          {value === '' ? placeholder : value}
        </span>
        {!disabled && (
          <button
            type="button"
            className="o-button o-button--ghost o-icon-button o-button--small"
            aria-label={`Modifier ${label}`}
            onClick={() => {
              setDraft(value);
              setError(undefined);
              setEditing(true);
            }}
          >
            <Pencil aria-hidden="true" size={14} />
          </button>
        )}
      </span>
    );
  const submit = async (event?: SyntheticEvent): Promise<void> => {
    event?.preventDefault();
    if (draft === value) {
      setEditing(false);
      return;
    }
    setPending(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setPending(false);
    }
  };
  return (
    <form className="o-inline-edit o-inline-edit--editing" onSubmit={(e) => void submit(e)}>
      <input
        ref={input}
        className="o-input"
        type={type}
        aria-label={label}
        value={draft}
        disabled={pending}
        aria-invalid={error !== undefined}
        onChange={(e) => {
          setDraft(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            setEditing(false);
          }
        }}
      />
      <button
        type="submit"
        className="o-button o-button--primary o-icon-button o-button--small"
        aria-label="Enregistrer"
        disabled={pending}
      >
        <Check aria-hidden="true" size={14} />
      </button>
      <button
        type="button"
        className="o-button o-button--ghost o-icon-button o-button--small"
        aria-label="Annuler"
        onClick={() => {
          setEditing(false);
        }}
      >
        <X aria-hidden="true" size={14} />
      </button>
      {error !== undefined && (
        <span className="o-error" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}

/** Aide contextuelle « ? » : courte explication dans une bulle, ouverte au clic ou au clavier. */
export function HelpTip({
  children,
  label = 'Aide',
}: {
  children: ReactNode;
  label?: string;
}): ReactElement {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className="o-help-tip" aria-label={label}>
          <CircleHelp aria-hidden="true" size={16} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="o-popover" sideOffset={6} collisionPadding={16}>
          {children}
          <Popover.Arrow className="o-popover__arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/* ------------------------------------------------------------------ */
/* Réglages                                                            */
/* ------------------------------------------------------------------ */

/** Grille de champs : 2 ou 3 colonnes sur PC, une au téléphone (fini les champs empilés). */
export function FieldGrid({
  children,
  columns = 2,
}: {
  children: ReactNode;
  columns?: 2 | 3;
}): ReactElement {
  return <div className={`o-field-grid o-field-grid--${String(columns)}`}>{children}</div>;
}

type SectionTone = 'default' | 'advanced' | 'danger';

/**
 * Section de réglages titrée, avec une description courte. `tone` :
 * - `default` : réglages courants, ouverts ;
 * - `advanced` : réglages avancés, **repliés** par défaut (`<details>`) ;
 * - `danger` : zone sensible (liseré rouge), actions irréversibles ou à fort impact.
 */
export function SettingsSection({
  title,
  description,
  icon: Icon,
  tone = 'default',
  actions,
  defaultOpen,
  children,
  id,
}: {
  title: string;
  description?: ReactNode;
  icon?: LucideIcon | undefined;
  tone?: SectionTone;
  /** Action propre à la section (« Ajouter »…), à droite du titre. */
  actions?: ReactNode;
  /** Section avancée ouverte d'emblée. */
  defaultOpen?: boolean;
  /** Absent : section réduite à son en-tête (action seule, zone sensible). */
  children?: ReactNode;
  id?: string;
}): ReactElement {
  const headingId = useId();
  const head = (
    <>
      <span className="o-settings-section__titles">
        <h2 id={headingId} className="o-settings-section__title">
          {tone === 'danger' ? (
            <TriangleAlert aria-hidden="true" size={18} />
          ) : (
            Icon !== undefined && <Icon aria-hidden="true" size={18} />
          )}
          {title}
          {tone === 'advanced' && <span className="o-badge o-badge--neutral">Avancé</span>}
        </h2>
        {description !== undefined && (
          <span className="o-settings-section__description">{description}</span>
        )}
      </span>
      {actions !== undefined && <span className="o-settings-section__actions">{actions}</span>}
    </>
  );
  const className = `o-settings-section o-settings-section--${tone}`;
  if (tone === 'advanced')
    return (
      <details className={className} open={defaultOpen} id={id}>
        <summary className="o-settings-section__header">
          <ChevronRight aria-hidden="true" size={18} className="o-settings-section__chevron" />
          {head}
        </summary>
        {children !== undefined && children !== null && (
          <div className="o-settings-section__body">{children}</div>
        )}
      </details>
    );
  return (
    <section className={className} aria-labelledby={headingId} id={id}>
      <header className="o-settings-section__header">{head}</header>
      {children !== undefined && children !== null && (
        <div className="o-settings-section__body">{children}</div>
      )}
    </section>
  );
}

/**
 * Ligne de réglage compacte : libellé et description à gauche, contrôle à droite (empilés
 * au téléphone). `defaultValue` affiche la valeur par défaut lisible ; `modified` marque la
 * ligne changée et non enregistrée ; `help` ajoute une aide « ? ». Le contrôle reçoit
 * l'identifiant et la description à relier (`children(id, describedBy)`), ou un nœud simple.
 */
export function SettingRow({
  label,
  description,
  defaultValue,
  help,
  modified = false,
  wide = false,
  children,
}: {
  label: string;
  description?: ReactNode;
  defaultValue?: ReactNode;
  help?: ReactNode;
  modified?: boolean;
  /** Contrôle large (texte long, liste) : sous le libellé plutôt qu'à droite. */
  wide?: boolean;
  children: ReactNode | ((id: string, describedBy: string | undefined) => ReactNode);
}): ReactElement {
  const id = useId();
  const descId = description === undefined && defaultValue === undefined ? undefined : `${id}-d`;
  const classes = ['o-setting-row'];
  if (wide) classes.push('o-setting-row--wide');
  if (modified) classes.push('o-setting-row--modified');
  return (
    <div className={classes.join(' ')}>
      <div className="o-setting-row__text">
        <span className="o-setting-row__label">
          <label htmlFor={id}>{label}</label>
          {help !== undefined && <HelpTip label={`Aide : ${label}`}>{help}</HelpTip>}
          {modified && <span className="o-setting-row__dot" title="Modifié, non enregistré" />}
        </span>
        {descId !== undefined && (
          <span id={descId} className="o-setting-row__description">
            {description}
            {defaultValue !== undefined && (
              <span className="o-setting-row__default">Par défaut : {defaultValue}</span>
            )}
          </span>
        )}
      </div>
      <div className="o-setting-row__control">
        {typeof children === 'function' ? children(id, descId) : children}
      </div>
    </div>
  );
}

/**
 * Barre d'enregistrement collante (bas de l'écran) : état « Modifications non
 * enregistrées » / « Enregistré », Annuler et Enregistrer, Ctrl/⌘+S. Elle n'apparaît que
 * lorsqu'il y a quelque chose à dire (modifié, en cours, enregistré, erreur).
 */
export function SaveBar({
  dirty,
  saving = false,
  saved = false,
  error,
  onSave,
  onReset,
  saveLabel = 'Enregistrer',
  count,
}: {
  dirty: boolean;
  saving?: boolean;
  /** Dernier enregistrement réussi (et rien de modifié depuis). */
  saved?: boolean;
  error?: string | undefined;
  /** Absent : bouton de soumission du formulaire englobant. */
  onSave?: (() => void) | undefined;
  onReset?: (() => void) | undefined;
  saveLabel?: string;
  /** Nombre de réglages modifiés. */
  count?: number | undefined;
}): ReactElement | null {
  const bar = useRef<HTMLDivElement>(null);
  useHotkey(
    'mod+s',
    () => {
      if (onSave !== undefined) onSave();
      else bar.current?.closest('form')?.requestSubmit();
    },
    dirty && !saving,
  );
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty]);
  if (!dirty && !saving && !saved && error === undefined) return null;
  const status =
    error ??
    (saving
      ? 'Enregistrement…'
      : dirty
        ? count === undefined
          ? 'Modifications non enregistrées'
          : `${String(count)} réglage${count > 1 ? 's' : ''} modifié${count > 1 ? 's' : ''}, non enregistré${count > 1 ? 's' : ''}`
        : 'Modifications enregistrées');
  const tone = error !== undefined ? 'critical' : dirty || saving ? 'warning' : 'ok';
  return (
    <div ref={bar} className={`o-savebar o-savebar--${tone}`}>
      <p className="o-savebar__status" role="status" aria-live="polite">
        {tone === 'ok' && <Check aria-hidden="true" size={16} />}
        {tone === 'critical' && <TriangleAlert aria-hidden="true" size={16} />}
        {status}
      </p>
      {(dirty || error !== undefined) && (
        <div className="o-savebar__actions">
          {onReset !== undefined && (
            <button
              type="button"
              className="o-button o-button--ghost"
              onClick={onReset}
              disabled={saving}
            >
              <RotateCcw aria-hidden="true" size={16} /> Annuler les modifications
            </button>
          )}
          <button
            type={onSave === undefined ? 'submit' : 'button'}
            className="o-button o-button--primary"
            disabled={saving || !dirty}
            onClick={onSave}
          >
            {saveLabel} <Kbd keys="mod+s" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Suivi d'un formulaire de réglages : valeur enregistrée, saisie, état modifié, dernier
 * enregistrement. `reset()` revient à la valeur enregistrée, `commit(v)` la remplace.
 */
export function useSettingsDraft<T>(saved: T | undefined): {
  draft: T | undefined;
  setDraft: (next: T) => void;
  update: <K extends keyof T>(key: K, value: T[K]) => void;
  reset: () => void;
  dirty: boolean;
  justSaved: boolean;
  markSaved: () => void;
} {
  const [draft, setDraftState] = useState<T | undefined>(saved);
  const [base, setBase] = useState<T | undefined>(saved);
  const [justSaved, setJustSaved] = useState(false);
  // Nouvelle valeur du serveur (comparée par contenu : elle peut être recalculée à chaque
  // rendu) : reprise tant que rien n'est en cours de saisie.
  const savedJson = JSON.stringify(saved);
  const [seen, setSeen] = useState(savedJson);
  if (savedJson !== seen) {
    setSeen(savedJson);
    if (JSON.stringify(draft) === JSON.stringify(base)) {
      setBase(saved);
      setDraftState(saved);
    }
  }
  const dirty = JSON.stringify(draft) !== JSON.stringify(base);
  return {
    draft,
    setDraft: (next) => {
      setJustSaved(false);
      setDraftState(next);
    },
    update: (key, value) => {
      setJustSaved(false);
      setDraftState((d) => (d === undefined ? d : { ...d, [key]: value }));
    },
    reset: () => {
      setDraftState(base);
    },
    dirty,
    justSaved: justSaved && !dirty,
    markSaved: () => {
      setBase(draft);
      setJustSaved(true);
    },
  };
}

/** Carte-lien d'un catalogue (réglages des modules, rubriques) : titre, description, liens. */
export function LinkCard({
  title,
  description,
  icon: Icon,
  to,
  links,
}: {
  title: string;
  description?: ReactNode;
  icon?: LucideIcon | undefined;
  to: string;
  links?: readonly { to: string; label: string }[] | undefined;
}): ReactElement {
  return (
    <article className="o-link-card">
      <Link to={to} className="o-link-card__main">
        {Icon !== undefined && (
          <span className="o-link-card__icon" aria-hidden="true">
            <Icon size={20} />
          </span>
        )}
        <span className="o-link-card__text">
          <span className="o-link-card__title">{title}</span>
          {description !== undefined && (
            <span className="o-link-card__description">{description}</span>
          )}
        </span>
        <ChevronRight aria-hidden="true" size={18} className="o-link-card__chevron" />
      </Link>
      {links !== undefined && links.length > 1 && (
        <ul className="o-link-card__links">
          {links.map((l) => (
            <li key={l.to}>
              <Link to={l.to}>{l.label}</Link>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
